/**
 * Constellation filters (doc 147 §5.3).
 *
 * The chip row prunes the graph by domain / tier / pack / isMinor. Filtering
 * never re-runs layout — coordinates are static — so `applyFilters` just
 * returns the set of visible entity ids and the view re-tints. The `isMinor`
 * filter is compliance-relevant: toggling it must instantly hide age-gated
 * entities.
 */
import type { AtlasManifest, SubscriptionTier, PackId } from '@atlas-manifest/types';

export interface ConstellationFilters {
  domain: string | 'all';
  tier: SubscriptionTier | 'all';
  pack: PackId | 'all';
  isMinor: 'any' | 'visible' | 'hidden';
}

export const defaultFilters: ConstellationFilters = {
  domain: 'all',
  tier: 'all',
  pack: 'all',
  isMinor: 'any',
};

/** True when any filter is set away from its default. */
export function filtersActive(filters: ConstellationFilters): boolean {
  return (
    filters.domain !== 'all' ||
    filters.tier !== 'all' ||
    filters.pack !== 'all' ||
    filters.isMinor !== 'any'
  );
}

/**
 * Return the set of entity ids that pass every active filter.
 *  - **domain** — the entity's own domain.
 *  - **pack** — the entity's domain must be gated by that pack.
 *  - **tier** — a `tier:<t>` tag on the entity, or the domain's tier gate.
 *  - **isMinor** — the entity's `isMinorVisible` flag.
 */
export function applyFilters(
  manifest: AtlasManifest,
  filters: ConstellationFilters,
): Set<string> {
  const domainById = new Map(manifest.domains.map((d) => [d.id, d]));
  const visible = new Set<string>();

  for (const entity of manifest.entities) {
    if (filters.domain !== 'all' && entity.domain !== filters.domain) continue;

    const domain = domainById.get(entity.domain);
    if (filters.pack !== 'all' && domain?.packGate !== filters.pack) continue;

    if (filters.tier !== 'all') {
      const taggedTier = entity.tags.includes(`tier:${filters.tier}`);
      const domainTier = domain?.tierGate === filters.tier;
      if (!taggedTier && !domainTier) continue;
    }

    if (filters.isMinor === 'visible' && !entity.isMinorVisible) continue;
    if (filters.isMinor === 'hidden' && entity.isMinorVisible) continue;

    visible.add(entity.id);
  }

  return visible;
}
